import { UserProfileRole } from '@@types/api.types';
import { IntelTechnologiesPagination } from '@components/core/intel-technologies-table/intel-technologies-pagination.component';
import { IntelTechnologiesTable } from '@components/core/intel-technologies-table/intel-technologies-table.component';
import { useIntelTechnologiesQueryParams } from '@components/core/intel-technologies-table/use-intel-technologies-query-params.hook';
import { MainLayout } from '@components/layouts/main.layout';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { useGetChallenge } from '@queries/challenges.query';
import { useIntelTechnologiesQuery } from '@queries/intel-technologies.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ChallengeHeader } from './components/challenge-header.component';
import { ChallengePhasesGrid } from './components/challenge-phases-grid.component';
import { IntelTechnologiesPhaseFilters } from './components/intel-technologies-phase-filters.component';
import { TechChallengeBarChart } from './components/tech-challenge-bar-chart.component';

export const TechChallengeDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: challenge, isPending } = useGetChallenge({ challengeId: id ?? '' });
  const { data: user, isPending: isUserPending } = useGetCurrentUser();

  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  const {
    DEFAULT_SORTING,
    handleLimitChange,
    page,
    phaseFilter,
    queryParams,
    search,
    setPage,
    setPhaseFilter,
    setSearch,
    setSorting,
    sortingState,
  } = useIntelTechnologiesQueryParams();

  const {
    data: technologiesData,
    isFetching,
    isPending: isTechnologiesPending,
  } = useIntelTechnologiesQuery(queryParams);

  const technologies = technologiesData?.technologies ?? [];
  const pagination = technologiesData?.pagination;

  useEffect(() => {
    if (!pagination) return;

    const current = page;
    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (current !== 1) void setPage(1);
      return;
    }

    if (current > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isPending || isUserPending || !challenge) {
    return (
      <PageviewTracker>
        <HelmetWrapper title="Challenge Details" />
        <MainLayout>
          <ChallengeHeader isLoading={isPending} />
        </MainLayout>
      </PageviewTracker>
    );
  }

  if (!isIntel) {
    return <Navigate to={routes.home.path} />;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title={`Challenge: ${challenge.name}`} />
      <MainLayout
        aside={
          <div className="flex flex-col gap-4">
            <ChallengeHeader
              challenge={challenge}
              isLoading={isPending}
            />
            <ChallengePhasesGrid challenge={challenge} />
          </div>
        }
        asideClassName="order-1 @min-[56rem]/main:order-1"
        columnsStartAtContainer="56rem"
        contentClassName="order-2 gap-4 @min-[56rem]/main:order-2"
      >
        <TechChallengeBarChart challenge={challenge} />

        <Tile header={{ title: 'Technologies' }}>
          <div className="flex flex-col gap-4">
            <IntelTechnologiesPhaseFilters
              onPhaseFilterChange={(value) => {
                void setPhaseFilter(value);
                void setPage(1);
              }}
              phases={challenge.phases ?? []}
              selectedPhaseFilter={phaseFilter}
            />

            <ClearableSearchInput
              className="w-full"
              placeholder="Search by technology name"
              value={search ?? ''}
              onChange={(value) => {
                void setSearch(value ? value : null);
                void setPage(1);
              }}
            />

            <IntelTechnologiesTable
              isLoading={isTechnologiesPending}
              onSortingChange={(state) => {
                if (!state || state.length === 0) {
                  void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                  return;
                }
                void setPage(1);
                void setSorting(state);
              }}
              sorting={sortingState}
              technologies={technologies}
            />

            {pagination && (
              <IntelTechnologiesPagination
                isFetching={isFetching}
                pagination={pagination}
                onPageChange={(nextPage) => void setPage(Math.max(1, nextPage))}
                onPageSizeChange={handleLimitChange}
              />
            )}
          </div>
        </Tile>
      </MainLayout>
    </PageviewTracker>
  );
};
